import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, Button, Buttons, CancelButton, Checkbox, Icon, SingleSelect } from '~/components';
import { useApi, useSession } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { colors, weights } from '~/styles';
import { ErrorPage, PageLoader } from '../pages';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 1.5rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 30rem;
  padding: 2rem;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey10};
  border-radius: 0.625rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: ${weights.light};
  line-height: 1.3;
  text-align: center;
`;

const ClientName = styled.span`
  font-weight: ${weights.bold};
`;

const Unverified = styled.div`
  margin-top: 0.5rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  text-align: center;
`;

const Message = styled.p`
  margin-top: 1rem;
  color: ${colors.danger};
  font-size: 0.875rem;
`;

const RedirectUri = styled.div`
  margin-top: 0.375rem;
  color: ${colors.black};
  font-family: monospace;
  font-weight: ${weights.bold};
  word-break: break-all;
`;

const WarningNote = styled.div`
  margin-top: 0.5rem;
`;

const Acknowledgement = styled.div`
  margin-top: 0.75rem;
`;

const WorkspaceOption = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;

  span {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const WorkspaceLogo = styled.img`
  width: 1.25rem;
  height: 1.25rem;
  object-fit: contain;
  flex-shrink: 0;
`;

// Shared by the closed control and the open menu so a workspace looks the same in both.
function workspaceDisplay(workspace) {
  if (!workspace) return null;

  return (
    <WorkspaceOption>
      {workspace.imageUrl ? (
        <WorkspaceLogo src={workspace.imageUrl} alt="" />
      ) : (
        <Avatar value={{ ...workspace, name: workspace.name?.charAt(0) }} size={20} isCircle hasBackground />
      )}
      <span>{workspace.name}</span>
    </WorkspaceOption>
  );
}

const Identified = styled.span`
  color: ${colors.grey40};
  text-transform: none;
`;

const Section = styled.div`
  margin-top: 2rem;
`;

const SectionLabel = styled.div`
  margin-bottom: 0.5rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  letter-spacing: 0.05rem;
  text-transform: uppercase;
`;

const Scope = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 0.75rem;
  font-size: 0.875rem;
`;

const ScopeIcon = styled(Icon)`
  margin-top: 0.15rem;
  margin-right: 0.625rem;
  color: ${colors.primary};
`;

const Warning = styled.div`
  display: flex;
  margin-top: 1.5rem;
  padding: 0.875rem 1rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  background-color: ${colors.warning10};
  border-radius: 0.3125rem;
`;

const WarningIcon = styled(Icon)`
  flex-shrink: 0;
  margin-top: 0.15rem;
  margin-right: 0.75rem;
  color: ${colors.warning};
`;

const Note = styled.p`
  margin-top: 2rem;
  color: ${colors.grey55};
  font-size: 0.8125rem;
  line-height: 1.5;
`;

const Actions = styled.div`
  margin-top: 2rem;
`;

export default function AuthorizeConnection() {
  useDocumentTitle('Authorize Connection');

  const api = useApi();
  const history = useHistory();
  const location = useLocation();
  const { isReady: isSessionReady, session } = useSession();

  const [request, setRequest] = useState();
  const [workspaceId, setWorkspaceId] = useState('');
  const [error, setError] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [trustsRedirect, setTrustsRedirect] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The authorization parameters travel in the query string, exactly as /api/oauth/authorize
  // handed them over. They are re-validated server-side on every call, so they are not trusted here.
  const params = Object.fromEntries(new URLSearchParams(location.search));

  useEffect(() => {
    if (!isSessionReady) return;

    // Sign in first, then come back to this same authorization request.
    if (!session) {
      const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
      history.replace(`/login?redirect=${redirect}`);
      return;
    }

    (async () => {
      try {
        const { data } = await api.www.oauth().authorizationRequest(params);
        setRequest(data);
        if (data.workspaces.length === 1) setWorkspaceId(data.workspaces[0].id);
      } catch (requestError) {
        setError(requestError);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionReady, session, location.search]);

  const decide = useCallback(
    async (approved) => {
      setIsSubmitting(true);
      setErrorMessage();

      try {
        const { data } = await api.www.oauth().consent({ ...params, approved, workspaceId });
        // Hand control back to the client that started the flow.
        window.location.replace(data.redirectUrl);
      } catch ({ message }) {
        // Shown in place rather than through `error`, which would replace the card and lose the
        // context the member needs to decide — which application is asking, and for what.
        setErrorMessage(message || 'There was a problem completing your request. Please try again.');
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, workspaceId, location.search],
  );

  if (error) return <ErrorPage publicSite />;
  if (!isSessionReady || !request) return <PageLoader />;

  const hasWorkspace = request.workspaces.length > 0;
  const hasSingleWorkspace = request.workspaces.length === 1;
  const selectedWorkspace = request.workspaces.find((workspace) => workspace.id === workspaceId);

  return (
    <Wrapper>
      <Card>
        <Title>
          Connect <ClientName>{request.client.name}</ClientName> to Ruddr
        </Title>

        {/* A dynamically registered client chooses its own name, so nothing stops one calling itself
            Ruddr. Only a metadata document ties the name to a domain the member can judge. */}
        {!request.client.isClientIdMetadataDocument && (
          <Unverified>This name was provided by the application and has not been verified by Ruddr.</Unverified>
        )}

        <Section>
          <SectionLabel>Workspace</SectionLabel>
          {hasWorkspace ? (
            /* Disabled rather than replaced when there is only one: the member still sees which
               workspace they are connecting, without being offered a choice that does not exist. */
            <SingleSelect
              value={workspaceId}
              disabled={hasSingleWorkspace}
              valueRenderer={() => workspaceDisplay(selectedWorkspace)}
              onChange={(event) => setWorkspaceId(event.target.value)}>
              {request.workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspaceDisplay(workspace)}
                </option>
              ))}
            </SingleSelect>
          ) : request.hasMemberSession ? (
            <Scope>
              <span>
                The Ruddr Assistant is not enabled for you in any of your workspaces. Ask a workspace admin to enable
                it, then try connecting again.
              </span>
            </Scope>
          ) : (
            <Scope>
              <span>
                An application can only be connected by a member signed in to Ruddr directly, not through an
                administrator's impersonation session. Sign in yourself and try connecting again.
              </span>
            </Scope>
          )}
        </Section>

        <Section>
          <SectionLabel>
            Through Ruddr, {request.client.name} will be able to
            {request.client.isClientIdMetadataDocument && <Identified> · {request.client.clientIdHost}</Identified>}
          </SectionLabel>
          {request.scopes.map(({ scope, description }) => (
            <Scope key={scope}>
              <ScopeIcon icon="check" />
              <span>{description}</span>
            </Scope>
          ))}
        </Section>

        {/* The full address, port included, because the member is being asked to recognise it rather
            than just read it — and an approval nobody has to act on is one nobody reads. */}
        <Warning>
          <WarningIcon icon="exclamation-triangle" />
          <div>
            <div>You will be redirected to the following location:</div>
            <RedirectUri>{request.client.redirectUri}</RedirectUri>
            {request.client.isLoopbackRedirect && (
              <WarningNote>
                That address is software running on your own computer. Ruddr cannot tell which application it is, so
                only continue if you just started this from {request.client.name}.
              </WarningNote>
            )}
            <Acknowledgement>
              <Checkbox
                checked={trustsRedirect}
                label="I recognize and trust this address"
                onChange={() => setTrustsRedirect((value) => !value)}
              />
            </Acknowledgement>
          </div>
        </Warning>

        <Note>
          {request.client.name} will act as you. It can only see and change what your Ruddr permissions already allow,
          and you can revoke this connection at any time from your profile.
        </Note>

        {errorMessage && <Message>{errorMessage}</Message>}

        <Actions>
          <Buttons align="right">
            <CancelButton disabled={isSubmitting} onClick={() => decide(false)} style={{ marginRight: 'auto' }}>
              Cancel
            </CancelButton>
            {/* `disabled` has to carry isSubmitting too: Button spreads props over its own
                `disabled={isLoading}`, so passing one here would otherwise re-enable the click and
                mint a second authorization code. */}
            <Button
              disabled={!workspaceId || !hasWorkspace || !trustsRedirect || isSubmitting}
              isLoading={isSubmitting}
              onClick={() => decide(true)}>
              Authorize
            </Button>
          </Buttons>
        </Actions>
      </Card>
    </Wrapper>
  );
}
